import React, {
  useState,
  createContext,
  useEffect,
  useContext,
  useCallback,
} from "react";
import {
  unstable_useBlocker,
  unstable_usePrompt,
  useLocation,
  useParams,
} from "react-router-dom";
import { emptyPage, trimPageData } from "../helpers";
import { APIProviderContext } from "../api";
import { toast } from "react-toastify";
import useInterval from "use-interval";
import _ from "lodash";
import { AccountContext } from "../account/account";
import { GrigoraDashboardContext } from "../dashboard/context";
import slugify from "slugify";
import Toast from "../toast/toast";
import { CorrectIcon } from "../../assets/ui-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RealtimeContext } from "../realtime";

const GrigoraPostEditorContext = createContext();

function GrigoraPostEditorProvider({ newPost: newPostRouter, ...props }) {
  const { projectID, postID: postIDRouter } = useParams();

  const [newPost, setNewPost] = useState(newPostRouter);
  const [postID, setPostID] = useState(postIDRouter);

  const {
    getCMSPostData,
    publishQueueCheck,
    getCMSPostContent,
    getRemarks,
    cmsEditorLoad,
  } = useContext(APIProviderContext) ?? {};
  const { domain } = useContext(GrigoraDashboardContext) ?? {};
  const { userAttributes } = useContext(AccountContext) ?? {};
  const [isLoading, setIsLoading] = useState(true);
  const [postData, setPostData] = useState(null);
  const [postDataCheckpoint, setPostDataCheckpoint] = useState(null);
  const [postContent, setPostContent] = useState(null);
  const [postContentCheckpoint, setPostContentCheckpoint] = useState(null);
  const [selectedRemark, setSelectedRemark] = useState([]);
  const [remarksData, setRemarksData] = useState({});
  const [keyboardShortcutModal, setKeyboardShortcutModal] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [leftCardToggle, setLeftCardToggle] = useState("");
  const [publishQueueID, setPublishQueueID] = useState("");
  const [isJoyrideSettingsActive, setIsJoyrideSettingsActive] = useState(false);
  const [isJoyrideRemarksActive, setIsJoyrideRemarksActive] = useState(false);
  const [isJoyrideActive, setIsJoyrideActive] = useState(
    localStorage.getItem("grigora-posteditor-tour") == "true" ||
      !localStorage.getItem("grigora-posteditor-tour")
      ? true
      : false,
    // true,
  );
  const [toggleRemarkPopover, setToggleRemarkPopover] = useState(false);

  const { socket, socketEvents } = useContext(RealtimeContext) ?? {};

  // Load from local storage
  const [isBlockSettingsVisible, setIsBlockSettingsVisible] = useState(
    localStorage.getItem("grigora-post-editor-isBlockSettingsVisible") ===
      "true",
  );

  // Set cache of isBlockSettingsVisible in localstorage
  useEffect(() => {
    localStorage.setItem(
      "grigora-post-editor-isBlockSettingsVisible",
      isBlockSettingsVisible,
    );
  }, [isBlockSettingsVisible]);

  const { state: routerState } = useLocation();

  const resetStates = () => {
    setIsLoading(true);
    setPostData(null);
    setPostDataCheckpoint(null);
    setPostContent(null);
    setPostContentCheckpoint(null);
    setIsPublishing(false);
    setPublishQueueID("");
    setRemarksData({});
    setSelectedRemark([]);
    setLeftCardToggle("");
    setFocusMode(false);
  };

  useEffect(() => {
    resetStates();
    if (newPost) {
      setPostData({
        author: userAttributes?.sub,
      });
      setPostContent({});
      setIsLoading(false);
    } else {
      cmsEditorLoad?.(projectID, postID)
        .then((data) => {
          setPostData(data?.postData);
          setPostDataCheckpoint(data?.postData);

          setPostContent(data?.postContent?.content);
          setPostContentCheckpoint(data?.postContent?.content);

          setRemarksData(data?.remarks);

          setIsLoading(false);
        })
        .catch((err) => {
          resetStates();
          setIsLoading(true);
        });
    }

    window.addEventListener("beforeunload", preventClose);

    return () => {
      resetStates();
      window.removeEventListener("beforeunload", preventClose);
    };
  }, []);

  const [scheduledTime, setScheduledTime] = useState({});
  const [isPostScheduled, setIsPostSchduled] = useState(false);

  const socketHandler = useCallback(
    (event) => {
      var queueID = event?.data?.queueID;

      if (projectID !== event?.data?.projectID) {
        return;
      }

      var isScheduledResponse = queueID?.startsWith?.("schedule-");

      if (publishQueueID || isScheduledResponse) {
        if (publishQueueID === queueID || isScheduledResponse) {
          if (isScheduledResponse) {
            // If event is not from current post ID
            var postIDRemote = event?.data?.meta?.posts ?? [];

            if (postIDRemote.indexOf(postID) === -1) {
              return;
            }
          }

          setPostData({
            ...postData,
            status: "published",
            last_published: Math.floor(new Date().getTime() / 1000),
          });
          setIsPublishing(false);
          toast.success(
            <>
              <span>
                Post Published.{" "}
                <a
                  target={"_blank"}
                  className="text-ui-dark-blue"
                  href={`https://${domain}/${
                    postData.slug
                      ? postData.slug
                      : slugify(postData.title ? postData.title : "", {
                          trim: false,
                          strict: true,
                          lower: true,
                        })
                  }`}
                  rel="noreferrer"
                >
                  View.
                </a>
              </span>
            </>,
            {
              autoClose: 5000,
            },
          );
          setIsPublishing(false);
          setPublishQueueID("");

          if (isScheduledResponse) {
            setScheduledTime({});
            setIsPostSchduled(false);

            // Fetch new post data and update slug and first_published
            getCMSPostData(projectID, postID)
              .then((data) => {
                setPostData({
                  ...postData,
                  slug: data?.slug,
                  first_published: data?.first_published,
                });
                setPostDataCheckpoint({
                  ...postData,
                  slug: data?.slug,
                  first_published: data?.first_published,
                });
              })
              .catch((err) => {
                console.log(err);
              });
          }
        }
      }
    },
    [
      projectID,
      postID,
      domain,
      publishQueueID,
      postData,
      setPostData,
      setIsPublishing,
      setPublishQueueID,
    ],
  );

  const remarkSocketHandler = useCallback(
    (event) => {
      if (
        event.type === "REMARK:NEW" &&
        event.projectID === projectID &&
        event.postID === postID
      ) {
        setRemarksData({
          ...remarksData,
          ...event.data,
        });
      } else if (
        event.type === "REMARK:UPDATE" &&
        event.projectID === projectID &&
        event.postID === postID
      ) {
        setRemarksData({
          ...remarksData,
          ...event.data,
        });
      } else if (
        event.type === "REMARK:DELETE" &&
        event.projectID === projectID &&
        event.postID === postID
      ) {
        setRemarksData({
          ...remarksData,
          ...event.data,
        });
      } else if (
        event.type === "REMARK:THREAD:DELETE" &&
        event.projectID === projectID &&
        event.postID === postID
      ) {
        var threadID = event.threadID;
        var temp = { ...remarksData };
        delete temp[threadID];
        delete temp["thread-" + threadID];
        setRemarksData(temp);
      }
    },
    [remarksData, setRemarksData, domain, postData, setPostData],
  );

  useEffect(() => {
    // Listen for messages
    if (socketEvents) {
      socketEvents.subscribe("PUBLISH_CMS_POST", socketHandler);
      socketEvents.subscribe("REMARK:NEW", remarkSocketHandler);
      socketEvents.subscribe("REMARK:UPDATE", remarkSocketHandler);
      socketEvents.subscribe("REMARK:DELETE", remarkSocketHandler);
      socketEvents.subscribe("REMARK:THREAD:DELETE", remarkSocketHandler);
    }

    return () => {
      if (socketEvents) {
        socketEvents.unsubscribe("PUBLISH_CMS_POST", socketHandler);
        socketEvents.unsubscribe("REMARK:NEW", remarkSocketHandler);
        socketEvents.unsubscribe("REMARK:UPDATE", remarkSocketHandler);
        socketEvents.unsubscribe("REMARK:DELETE", remarkSocketHandler);
        socketEvents.unsubscribe("REMARK:THREAD:DELETE", remarkSocketHandler);
      }
    };
  }, [socket, socketHandler]);

  useEffect(() => {
    window.addEventListener("beforeunload", preventClose);
    return () => {
      window.removeEventListener("beforeunload", preventClose);
    };
  }, [postContent, postContentCheckpoint, postData, postDataCheckpoint]);

  const updateCheckpoint = () => {
    setPostDataCheckpoint(postData);
    setPostContentCheckpoint(postContent);
  };

  function preventClose(e) {
    if (_.isEqual(postContent, postContentCheckpoint)) {
      return;
    }
    if (_.isEqual(postData, postDataCheckpoint)) {
      return;
    }
    e.preventDefault();
    e.returnValue = "";
  }

  const closeLeftBar = () => {
    if (leftCardToggle) {
      setLeftCardToggle("");
    }
  };

  function enterFullScreen(element) {
    if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if (element.mozRequestFullScreen) {
      element.mozRequestFullScreen(); // Firefox
    } else if (element.webkitRequestFullscreen) {
      element.webkitRequestFullscreen(); // Safari
    } else if (element.msRequestFullscreen) {
      element.msRequestFullscreen(); // IE/Edge
    }
  }

  function exitFullscreen() {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    }
  }

  return (
    <GrigoraPostEditorContext.Provider
      value={{
        enterFullScreen,
        exitFullscreen,
        keyboardShortcutModal,
        setKeyboardShortcutModal,
        newPost,
        setNewPost,
        isLoading,
        projectID,
        postID,
        setPostID,
        postData,
        setPostData,
        postDataCheckpoint,
        setPostDataCheckpoint,
        postContent,
        setPostContent,
        postContentCheckpoint,
        setPostContentCheckpoint,
        selectedRemark,
        setSelectedRemark,
        remarksData,
        setRemarksData,
        isBlockSettingsVisible,
        setIsBlockSettingsVisible,
        isPublishing,
        setIsPublishing,
        publishQueueID,
        setPublishQueueID,
        updateCheckpoint,
        routerState,
        leftCardToggle,
        setLeftCardToggle,
        closeLeftBar,
        focusMode,
        setFocusMode,
        isJoyrideActive,
        setIsJoyrideActive,
        isJoyrideSettingsActive,
        setIsJoyrideSettingsActive,
        isJoyrideRemarksActive,
        setIsJoyrideRemarksActive,
        toggleRemarkPopover,
        setToggleRemarkPopover,
        scheduledTime,
        setScheduledTime,
        isPostScheduled,
        setIsPostSchduled,
      }}
    >
      {props.children}
    </GrigoraPostEditorContext.Provider>
  );
}

export { GrigoraPostEditorProvider, GrigoraPostEditorContext };
